/**
 * Hook för DataReplay
 * Hanterar state och logik för återläsning av historiska GPS-data
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '@/services/api/client';
import {
  ReplayStatus,
  AvailableDate,
  VehicleOption,
  DataStatusResult,
} from '@/types/dataReplay';
import type { Organization } from '@/types/organizations';

/** Org + alla underorganisationer (via parent_id, samma princip som listor i admin). */
function buildDescendantOrgIdSet(all: Organization[], rootId: string): Set<string> {
  const ids = new Set<string>([String(rootId)]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const o of all) {
      if (o.parent_id != null && ids.has(String(o.parent_id)) && !ids.has(String(o.id))) {
        ids.add(String(o.id));
        changed = true;
      }
    }
  }
  return ids;
}

export function useDataReplay() {
  // State
  const [availableDates, setAvailableDates] = useState<AvailableDate[]>([]);
  const [availableImeis, setAvailableImeis] = useState<VehicleOption[]>([]);
  const [jobs, setJobs] = useState<ReplayStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fullResetInProgress, setFullResetInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [dataStatus, setDataStatus] = useState<DataStatusResult | null>(null);
  const [dataStatusLoading, setDataStatusLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  
  // Form state
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedImei, setSelectedImei] = useState<string>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Search state for vehicle dropdown
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [isVehicleDropdownOpen, setIsVehicleDropdownOpen] = useState(false);
  const vehicleDropdownRef = useRef<HTMLDivElement>(null);

  // Load data functions
  const loadAvailableDates = useCallback(async () => {
    try {
      const response = await api.get('/api/sysadmin/replay/dates');
      if (response.data.success) {
        setAvailableDates(response.data.data);
      }
    } catch {
      // Failed to load dates - handled silently
    }
  }, []);

  /** IMEI med råfiler i spannet som också passerar device_vehicle_history (samma urval som replay). */
  const loadAvailableImeisForRange = useCallback(async (from: string, to: string) => {
    try {
      const response = await api.get('/api/sysadmin/replay/imeis', {
        params: { fromDate: from, toDate: to },
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        const raw = response.data.data as Array<{
          imei: string;
          regNumber?: string | null;
          organizationId?: string;
        }>;
        setAvailableImeis(
          raw.map((d) => ({
            imei: d.imei,
            regNumber: d.regNumber ?? null,
            organizationId:
              d.organizationId != null && String(d.organizationId).trim() !== ''
                ? String(d.organizationId)
                : undefined,
          }))
        );
      }
    } catch {
      // Failed to load IMEIs - handled silently
    }
  }, []);

  const loadAllVehicles = useCallback(async () => {
    try {
      const response = await api.get('/api/devices?limit=5000');
      if (response.data.success && response.data.data) {
        const vehicles: VehicleOption[] = response.data.data
          .filter((d: { imei?: string }) => d.imei)
          .map((d: { imei: string; vehicle_name?: string; registration_number?: string; organization_id?: string }) => ({
            imei: d.imei,
            regNumber: d.vehicle_name || d.registration_number || null,
            organizationId:
              d.organization_id != null && String(d.organization_id).trim() !== ''
                ? String(d.organization_id)
                : undefined,
          }));
        setAvailableImeis(vehicles);
      }
    } catch {
      // Failed to load all vehicles - handled silently
    }
  }, []);

  const loadOrganizations = useCallback(async () => {
    setOrganizationsLoading(true);
    try {
      const response = await api.get('/api/organizations');
      const orgs = response.data?.success && Array.isArray(response.data.data) ? response.data.data : [];
      setOrganizations(orgs);
    } catch {
      setOrganizations([]);
    } finally {
      setOrganizationsLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    try {
      const response = await api.get('/api/sysadmin/replay/jobs');
      if (response.data.success) {
        setJobs(response.data.data);
      }
    } catch {
      // Failed to load jobs - handled silently
    }
  }, []);

  const loadDataStatus = useCallback(async () => {
    setDataStatusLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/sysadmin/replay/data-status');
      if (response.data.success && response.data.data) {
        setDataStatus(response.data.data);
      } else {
        setError(response.data?.error || 'Kunde inte hämta data-status');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
      setDataStatus(null);
    } finally {
      setDataStatusLoading(false);
    }
  }, []);

  // Load data on mount (IMEI-lista sätts av useEffect nedan: alla fordon tills från-datum väljs)
  useEffect(() => {
    loadAvailableDates();
    loadJobs();
    void loadOrganizations();
    const interval = setInterval(loadJobs, 5000);
    return () => clearInterval(interval);
  }, [loadAvailableDates, loadJobs, loadOrganizations]);

  // Clear success message after 8 seconds
  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 8000);
    return () => clearTimeout(t);
  }, [successMessage]);

  // IMEI-lista: fil + device_vehicle_history för valt spann (till = från om bara från-datum är satt)
  useEffect(() => {
    if (!fromDate) {
      void loadAllVehicles();
      return;
    }
    const to = toDate || fromDate;
    void loadAvailableImeisForRange(fromDate, to);
  }, [fromDate, toDate, loadAvailableImeisForRange, loadAllVehicles]);

  const scopedOrgIds = useMemo(() => {
    if (!selectedOrganizationId || organizations.length === 0) return null;
    return buildDescendantOrgIdSet(organizations, selectedOrganizationId);
  }, [selectedOrganizationId, organizations]);

  useEffect(() => {
    if (!selectedOrganizationId || organizations.length === 0) {
      setSelectedImei('');
      setVehicleSearch('');
      return;
    }
    const scoped = buildDescendantOrgIdSet(organizations, selectedOrganizationId);
    if (!selectedImei) return;
    const dev = availableImeis.find((d) => d.imei === selectedImei);
    if (!dev?.organizationId || !scoped.has(String(dev.organizationId))) {
      setSelectedImei('');
      setVehicleSearch('');
    }
  }, [selectedOrganizationId, organizations, availableImeis, selectedImei]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (vehicleDropdownRef.current && !vehicleDropdownRef.current.contains(event.target as Node)) {
        setIsVehicleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Actions
  const startReplay = useCallback(async () => {
    if (!fromDate || !toDate) {
      setError('Välj från- och till-datum');
      return;
    }
    if (!selectedOrganizationId) {
      setError('Välj organisation');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        organizationId: selectedOrganizationId,
        imei: selectedImei || undefined,
        fromDate,
        toDate,
      };
      // Enskilt fordon: batch-replay (samma logik som gamla systemet, sparar trippar direkt).
      // Hela org utan IMEI: kö-replay via workers.
      const endpoint = selectedImei
        ? '/api/sysadmin/replay/batch'
        : '/api/sysadmin/replay/start';
      const response = await api.post(endpoint, payload, {
        timeout: selectedImei ? 600_000 : 120_000,
      });

      if (response.data.success) {
        loadJobs();
        setFromDate('');
        setToDate('');
        setSelectedImei('');
        setVehicleSearch('');
      } else {
        setError(response.data.error);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedImei, selectedOrganizationId, loadJobs]);

  const stopReplayJob = useCallback(async (replayId: string) => {
    setError(null);
    try {
      const response = await api.post(`/api/sysadmin/replay/stop/${encodeURIComponent(replayId)}`);
      if (response.data.success) {
        loadJobs();
      } else {
        setError(response.data.error || 'Kunde inte stoppa replay');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  }, [loadJobs]);

  const runFullReset = useCallback(async () => {
    if (!confirm('Full reset tar bort ALL realtidsavledd data:\n• Trippar, GPS-chunks, eco-events\n• vehicle_daily_stats, trip_state, dedup-cache\n• Tömmer replay- och GPS-köer\n\nFordon och enheter behålls. Fortsätt?')) {
      return;
    }

    setLoading(true);
    setFullResetInProgress(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.post(
        '/api/sysadmin/replay/full-reset',
        { confirm: true, purgeQueues: true },
        { timeout: 300000 }
      );

      if (response.data.success) {
        setSuccessMessage('Full reset utförd. All realtidsavledd data rensad.');
        loadJobs();
        loadAvailableDates();
      } else {
        setError(response.data.error || 'Full reset misslyckades');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Full reset misslyckades');
    } finally {
      setLoading(false);
      setFullResetInProgress(false);
    }
  }, [loadJobs, loadAvailableDates]);

  const startReplayAll = useCallback(async () => {
    if (!confirm('Detta kommer:\n1. Stoppa TCP gateway\n2. Rensa ALL data\n3. Läsa om alla bin-filer\n4. Starta TCP igen\n\nÄr du säker?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/api/sysadmin/replay/full');

      if (response.data.success) {
        loadJobs();
      } else {
        setError(response.data.error);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [loadJobs]);

  // Vehicle search handlers
  const handleVehicleSelect = useCallback((imei: string) => {
    setSelectedImei(imei);
    const vehicle = availableImeis.find(v => v.imei === imei);
    if (vehicle) {
      setVehicleSearch(vehicle.regNumber ? `${vehicle.imei} (${vehicle.regNumber})` : vehicle.imei);
    } else {
      setVehicleSearch('');
    }
    setIsVehicleDropdownOpen(false);
  }, [availableImeis]);

  const handleVehicleSearchChange = useCallback((value: string) => {
    setVehicleSearch(value);
    setIsVehicleDropdownOpen(true);
    
    if (!value) {
      setSelectedImei('');
    } else {
      const exactMatch = availableImeis.find(v => 
        v.imei === value || 
        (v.regNumber && value === `${v.imei} (${v.regNumber})`)
      );
      if (exactMatch) {
        setSelectedImei(exactMatch.imei);
      }
    }
  }, [availableImeis]);

  const handleVehicleSearchFocus = useCallback(() => {
    setIsVehicleDropdownOpen(true);
    if (selectedImei && !vehicleSearch) {
      const vehicle = availableImeis.find(v => v.imei === selectedImei);
      if (vehicle) {
        setVehicleSearch(vehicle.regNumber ? `${vehicle.imei} (${vehicle.regNumber})` : vehicle.imei);
      }
    }
  }, [selectedImei, vehicleSearch, availableImeis]);

  const handleVehicleSearchBlur = useCallback(() => {
    if (selectedImei && !vehicleSearch) {
      const vehicle = availableImeis.find(v => v.imei === selectedImei);
      if (vehicle) {
        setVehicleSearch(vehicle.regNumber ? `${vehicle.imei} (${vehicle.regNumber})` : vehicle.imei);
      }
    }
  }, [selectedImei, vehicleSearch, availableImeis]);

  const clearVehicleSelection = useCallback(() => {
    setSelectedImei('');
    setVehicleSearch('');
    setIsVehicleDropdownOpen(false);
  }, []);

  /** Replay för alla IMEI i vald organisation (tomt IMEI i API). */
  const selectAllVehiclesInOrg = useCallback(() => {
    setSelectedImei('');
    setVehicleSearch('Alla fordon i vald organisation');
    setIsVehicleDropdownOpen(false);
  }, []);

  const selectDate = useCallback((date: string) => {
    setFromDate(date);
    setToDate(date);
  }, []);

  // Computed values
  const filteredVehicles = useMemo(() => {
    const searchLower = vehicleSearch.toLowerCase();
    let list = availableImeis;
    if (!selectedOrganizationId || !scopedOrgIds) {
      list = [];
    } else {
      list = list.filter(
        (v) =>
          v.organizationId != null &&
          v.organizationId !== '' &&
          scopedOrgIds.has(String(v.organizationId))
      );
    }
    return list.filter((vehicle) => {
      const imeiMatch = vehicle.imei.toLowerCase().includes(searchLower);
      const regMatch = vehicle.regNumber?.toLowerCase().includes(searchLower);
      return imeiMatch || regMatch;
    });
  }, [availableImeis, vehicleSearch, scopedOrgIds, selectedOrganizationId]);

  return {
    // Data
    availableDates,
    availableImeis,
    jobs,
    dataStatus,
    filteredVehicles,
    
    // Loading states
    loading,
    dataStatusLoading,
    fullResetInProgress,
    organizationsLoading,
    
    // Messages
    error,
    successMessage,
    
    // Form state
    selectedOrganizationId,
    selectedImei,
    fromDate,
    toDate,
    vehicleSearch,
    isVehicleDropdownOpen,
    vehicleDropdownRef,
    
    // Setters
    setFromDate,
    setToDate,
    setSelectedOrganizationId,
    setIsVehicleDropdownOpen,
    
    // Actions
    startReplay,
    stopReplayJob,
    runFullReset,
    startReplayAll,
    loadDataStatus,
    handleVehicleSelect,
    handleVehicleSearchChange,
    handleVehicleSearchFocus,
    handleVehicleSearchBlur,
    clearVehicleSelection,
    selectAllVehiclesInOrg,
    selectDate,
  };
}
